import React from 'react';

import { createPropTypes } from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import type { ResponsiveValue } from 'styled-system';
import { style } from 'styled-system';

import type { CommonProps } from './constants';
import { COMMON } from './constants';
import type { Theme } from './theme';
import theme from './theme';

type Scale = '1:1' | '4:1' | '3:1' | '2:1' | '16:9' | '4:3' | 'cinema';

const scale: Readonly<Record<Scale, number>> = {
  '1:1': 1,
  '4:1': 1 / 4,
  '3:1': 1 / 3,
  '2:1': 1 / 2,
  '16:9': 9 / 16,
  '4:3': 3 / 4,
  cinema: 1 / 2.35,
};

const aspectRatioStyle = style({
  // React prop name
  prop: 'aspectRatio',
  // The corresponding CSS property (defaults to prop argument)
  cssProperty: 'padding-bottom',
  // key for theme values
  key: 'aspectRatio',
  // accessor function for transforming the value
  // eslint-disable-next-line security/detect-object-injection
  transformValue: (n: Scale) => `${scale[n] * 100}%`,
  // add a fallback scale object or array, if theme is not present
  scale,
});
(aspectRatioStyle as any).propTypes = createPropTypes(['aspectRatio']);

type OuterProps = CommonProps & { theme?: typeof theme };

const AspectRatioOuter = styled.div<OuterProps>`
  display: block;
  overflow: hidden;
  position: relative;
  ${COMMON};
`;

type InnerProps = { aspectRatio?: ResponsiveValue<Scale, Theme> };

const AspectRatioInner = styled.div<InnerProps>`
  display: block;
  width: 100%;
  ${aspectRatioStyle};
`;

const AspectRatioChildren = styled.div`
  &,
  & iframe,
  & embed,
  & object,
  & video {
    bottom: 0;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }
`;

type Props = OuterProps & InnerProps;

// eslint-disable-next-line @typescript-eslint/no-shadow
function AspectRatio({ aspectRatio, children, theme, color, ...rest }: React.PropsWithChildren<Props>): JSX.Element {
  return (
    // issue with color typings: https://www.thetopsites.net/article/53711454.shtml
    <AspectRatioOuter theme={theme} color={color as any} {...rest}>
      <AspectRatioInner aspectRatio={aspectRatio} theme={theme} />
      <AspectRatioChildren>{children}</AspectRatioChildren>
    </AspectRatioOuter>
  );
}

AspectRatio.defaultProps = {
  aspectRatio: '1:1', // eslint-disable-line react/default-props-match-prop-types
  theme,
  children: null,
};

AspectRatio.propTypes = {
  children: PropTypes.node,
  theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  ...(aspectRatioStyle as any).propTypes,
};

AspectRatioOuter.displayName = 'AspectRatioOuter';
AspectRatioInner.displayName = 'AspectRatioInner';
AspectRatioChildren.displayName = 'AspectRatioChildren';
AspectRatio.displayName = 'AspectRatio';

export { AspectRatio };
